/**
 * Authentication Context
 * Provides authentication state and role-based access control throughout the app
 */

import React, { createContext, useContext, useReducer, useEffect } from "react";
import { apiService, apiClient } from "../services/api/apiService";
import { setSentryUser } from "../utils/sentry";

// Initial state
const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  permissions: [],
  role: null,
  organization: null,
  team: null,
};

// Action types
const AuthActionTypes = {
  LOGIN_START: "LOGIN_START",
  LOGIN_SUCCESS: "LOGIN_SUCCESS",
  LOGIN_FAILURE: "LOGIN_FAILURE",
  LOGOUT: "LOGOUT",
  SET_LOADING: "SET_LOADING",
  UPDATE_PERMISSIONS: "UPDATE_PERMISSIONS",
  UPDATE_PROFILE: "UPDATE_PROFILE",
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AuthActionTypes.LOGIN_START:
      return {
        ...state,
        isLoading: true,
      };

    case AuthActionTypes.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        permissions: action.payload.user.permissions || [],
        role: action.payload.user.role,
        organization: action.payload.user.organization,
        team: action.payload.user.team,
      };

    case AuthActionTypes.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        permissions: [],
        role: null,
        organization: null,
        team: null,
      };

    case AuthActionTypes.LOGOUT:
      return {
        ...initialState,
        isLoading: false,
      };

    case AuthActionTypes.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case AuthActionTypes.UPDATE_PERMISSIONS:
      return {
        ...state,
        permissions: action.payload,
      };

    case AuthActionTypes.UPDATE_PROFILE:
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      };

    default:
      return state;
  }
};

// Context
const AuthContext = createContext();

// Provider component
export const AuthProvider = ({ children, initialToken, initialUser }) => {
  const [state, dispatch] = useReducer(authReducer, {
    ...initialState,
    user: initialUser || null,
    token: initialToken || null,
    // Phase 1.1: Tokens in HttpOnly cookies - only check user existence
    isAuthenticated: Boolean(initialUser),
    isLoading: true, // Start as loading to prevent redirect loops
  });

  // Error reports carry the signed-in user's id (never their email or name)
  const signedInUserId = state.user?.id;
  useEffect(() => {
    setSentryUser(signedInUserId);
  }, [signedInUserId]);

  // Initialize authentication state from localStorage (only user data, not tokens)
  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      try {
        // Only check localStorage for user data - tokens are now in HttpOnly cookies
        const user = localStorage.getItem("user");

        if (user) {
          JSON.parse(user); // validate JSON

          try {
            const profileResponse = await apiService.getUserProfile();
            const rawUser = profileResponse.data || profileResponse;

            // Ensure `role` is always populated — the profile endpoint may return
            // `role_name` only, but all frontend permission checks use `user.role`.
            const verifiedUser = rawUser.role
              ? rawUser
              : { ...rawUser, role: rawUser.role_name };

            if (!isMounted) {
              return;
            }

            localStorage.setItem("user", JSON.stringify(verifiedUser));

            dispatch({
              type: AuthActionTypes.LOGIN_SUCCESS,
              payload: {
                user: verifiedUser,
                token: null,
              },
            });
          } catch (profileError) {
            if (!isMounted) {
              return;
            }

            localStorage.removeItem("user");
            localStorage.removeItem("token"); // also clear stale Bearer token
            dispatch({ type: AuthActionTypes.LOGIN_FAILURE });
          }
        } else {
          // No user found - set loading to false
          if (isMounted) {
            dispatch({ type: AuthActionTypes.SET_LOADING, payload: false });
          }
        }
      } catch (error) {
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        if (isMounted) {
          dispatch({ type: AuthActionTypes.LOGIN_FAILURE });
        }
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  // Login function
  // Supports both: credentials-based (email/password) and token-based (OAuth/OTP)
  const login = (tokenOrCredentials, user = null) => {
    try {
      dispatch({ type: AuthActionTypes.LOGIN_START });

      // Case 1: HttpOnly Cookie flow - login(null, user)
      // Called after successful API login - token is already in HttpOnly cookie
      if ((tokenOrCredentials === null || tokenOrCredentials === undefined) && user) {
        localStorage.setItem("user", JSON.stringify(user));

        dispatch({
          type: AuthActionTypes.LOGIN_SUCCESS,
          payload: {
            user: user,
            token: null, // Token is in HttpOnly cookie
          },
        });

        return { success: true, data: { user } };
      }

      // Case 2: Token-based login (OAuth, Phone OTP) - DEPRECATED
      // Called as: login(token, user)
      if (typeof tokenOrCredentials === "string" && user) {
        // Old flow - store only user, token is in HttpOnly cookie
        localStorage.setItem("user", JSON.stringify(user));

        dispatch({
          type: AuthActionTypes.LOGIN_SUCCESS,
          payload: {
            user: user,
            token: null, // Token is in HttpOnly cookie
          },
        });

        return { success: true, data: { user } };
      }

      // Case 3: Credentials object - login({ user })
      // Called as: login({ email, password }) or login({ user })
      const credentials = tokenOrCredentials;
      if (!credentials || typeof credentials !== "object") {
        throw new Error("Invalid login parameters");
      }

      // Store only user info, token is in HttpOnly cookie
      if (credentials.user) {
        localStorage.setItem("user", JSON.stringify(credentials.user));

        dispatch({
          type: AuthActionTypes.LOGIN_SUCCESS,
          payload: {
            user: credentials.user,
            token: null, // Token is in HttpOnly cookie
          },
        });

        return { success: true, data: { user: credentials.user } };
      }

      // If we reach here, credentials don't have user info
      dispatch({ type: AuthActionTypes.LOGIN_FAILURE });
      return { success: false, error: "Invalid login data" };
    } catch (error) {
      dispatch({ type: AuthActionTypes.LOGIN_FAILURE });
      return { success: false, error: error.message || "Login failed" };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Call logout endpoint to clear HttpOnly cookies on server
      await apiClient.post("/auth/logout");
    } catch (error) {
      // Logout endpoint not critical, ignore errors

    }

    // Clear ALL storage on client
    localStorage.clear();
    sessionStorage.clear();
    // Token cookie will be cleared by server

    dispatch({ type: AuthActionTypes.LOGOUT });
  };

  // Permission checking functions
  const hasPermission = (permission) => {
    if (!state.permissions || !Array.isArray(state.permissions)) return false;

    // Check for exact permission or wildcard
    return state.permissions.some((userPerm) => {
      if (userPerm === permission) return true;
      if (userPerm.endsWith("*")) {
        return permission.startsWith(userPerm.slice(0, -1));
      }
      return false;
    });
  };

  const hasRole = (role) => {
    return state.role === role;
  };

  const hasAnyRole = (roles) => {
    return Array.isArray(roles) ? roles.includes(state.role) : false;
  };

  // Role hierarchy check
  const hasMinimumRole = (minimumRole) => {
    const roleHierarchy = {
      super_admin: 6,
      platform_admin: 5,
      organization_admin: 4,
      team_manager: 3,
      coach: 2,
      player: 1,
      fan: 0,
    };

    const userRoleLevel = roleHierarchy[state.role] || 0;
    const requiredLevel = roleHierarchy[minimumRole] || 0;

    return userRoleLevel >= requiredLevel;
  };

  // Organization and team access checks
  const hasOrganizationAccess = (organizationId) => {
    if (!organizationId) return true;
    if (hasRole("super_admin") || hasRole("platform_admin")) return true;
    return state.organization?.id === organizationId;
  };

  const hasTeamAccess = (teamId) => {
    if (!teamId) return true;
    if (hasMinimumRole("organization_admin")) return true;
    return state.team?.id === teamId;
  };

  // Update permissions
  const updatePermissions = (permissions) => {
    dispatch({
      type: AuthActionTypes.UPDATE_PERMISSIONS,
      payload: permissions,
    });
  };

  // Update user profile
  const updateProfile = (profileData) => {
    const updatedUser = { ...state.user, ...profileData };
    localStorage.setItem("user", JSON.stringify(updatedUser));

    dispatch({
      type: AuthActionTypes.UPDATE_PROFILE,
      payload: profileData,
    });
  };

  const value = {
    // State
    ...state,

    // Actions
    login,
    logout,
    updatePermissions,
    updateProfile,

    // Permission checks
    hasPermission,
    hasRole,
    hasAnyRole,
    hasMinimumRole,
    hasOrganizationAccess,
    hasTeamAccess,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
